import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Plus, Play, Pause, Trash2, Edit, Upload, Clock, Database,
  FileSpreadsheet, FolderOpen, ChevronLeft, Loader2,
  CheckCircle, XCircle, AlertTriangle, Zap, History, Settings,
  FolderSync, Cloud, HardDrive, RefreshCw, Power, Timer,
  Link2, Unlink, ChevronRight, ArrowLeft, File, Folder
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FRECUENCIAS = [
  { value: "manual", label: "Manual (solo carga manual)" },
  { value: "cada_30_min", label: "Cada 30 minutos" },
  { value: "cada_hora", label: "Cada hora" },
  { value: "diario", label: "Diario" },
  { value: "semanal", label: "Semanal" },
  { value: "mensual", label: "Mensual" },
];

const ORIGENES = [
  { value: "manual", label: "Carga manual (subir archivo)", icon: Upload, available: true },
  { value: "local", label: "Carpeta local (sincronización automática)", icon: HardDrive, available: true },
  { value: "onedrive", label: "OneDrive / SharePoint", icon: Cloud, available: true },
  { value: "google_drive", label: "Google Drive (próximamente)", icon: Cloud, available: false },
];

// ============== FILE SYSTEM ACCESS API HELPERS ==============

const DB_NAME = "automatizaciones_fs";
const STORE_NAME = "dir_handles";

function openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveDirHandle(flowId, handle) {
  const idb = await openIDB();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(handle, `flow_${flowId}`);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getDirHandle(flowId) {
  const idb = await openIDB();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(`flow_${flowId}`);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function removeDirHandle(flowId) {
  const idb = await openIDB();
  return new Promise((resolve, reject) => {
    const tx = idb.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(`flow_${flowId}`);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

const supportsFileSystemAccess = typeof window !== "undefined" && "showDirectoryPicker" in window;

// ============== MAIN PAGE ==============

export default function AutomatizacionesPage() {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [view, setView] = useState("list");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const syncIntervalRef = useRef(null);
  const [msConfig, setMsConfig] = useState(null);
  const [msConnection, setMsConnection] = useState(null);
  const [showMsConfig, setShowMsConfig] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchFlows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/automatizaciones/`, getAuthHeaders());
      setFlows(res.data);
    } catch {
      toast.error("Error cargando automatizaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMsStatus = useCallback(async () => {
    try {
      const [configRes, connRes] = await Promise.all([
        axios.get(`${API}/api/microsoft/config`, getAuthHeaders()),
        axios.get(`${API}/api/microsoft/connection`, getAuthHeaders()),
      ]);
      setMsConfig(configRes.data);
      setMsConnection(connRes.data);
    } catch { /* ignore if user is not admin */ }
  }, []);

  useEffect(() => { fetchFlows(); fetchMsStatus(); }, [fetchFlows, fetchMsStatus]);

  // Check for OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("microsoft") === "connected") {
      toast.success("Cuenta de Microsoft conectada exitosamente");
      fetchMsStatus();
      window.history.replaceState({}, "", window.location.pathname);
    }
    const msError = params.get("microsoft_error");
    if (msError) {
      toast.error(`Error de Microsoft: ${decodeURIComponent(msError)}`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchMsStatus]);

  // Background sync: check for pending local executions
  useEffect(() => {
    const checkPendingSync = async () => {
      try {
        const localFlows = flows.filter(
          f => f.origen?.tipo === "local" && f.estado === "activo" && f.pending_execution
        );
        for (const flow of localFlows) {
          await syncLocalFlow(flow);
        }
      } catch (e) {
        console.error("Background sync error:", e);
      }
    };

    syncIntervalRef.current = setInterval(checkPendingSync, 30000);
    return () => clearInterval(syncIntervalRef.current);
  }, [flows]);

  const syncLocalFlow = async (flow) => {
    if (!supportsFileSystemAccess) return;
    try {
      const handle = await getDirHandle(flow.id);
      if (!handle) return;

      const permission = await handle.queryPermission({ mode: "read" });
      if (permission !== "granted") {
        const req = await handle.requestPermission({ mode: "read" });
        if (req !== "granted") return;
      }

      const pattern = flow.origen?.nombre_archivo || "*.xlsx";
      const extensions = pattern.replace("*", "").split(",").map(e => e.trim().toLowerCase());

      for await (const entry of handle.values()) {
        if (entry.kind !== "file") continue;
        const name = entry.name.toLowerCase();
        const matches = extensions.some(ext => name.endsWith(ext));
        if (!matches) continue;

        const file = await entry.getFile();
        // Only sync files modified in the last interval + buffer
        const ageMinutes = (Date.now() - file.lastModified) / 60000;
        if (ageMinutes > 120) continue;

        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/api/automatizaciones/${flow.id}/upload`, formData, {
          ...getAuthHeaders(),
          headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
        });

        toast.success(`Archivo "${file.name}" sincronizado para ${flow.nombre}`);
      }

      // Mark execution complete
      await axios.post(`${API}/api/automatizaciones/${flow.id}/complete-execution`, {}, getAuthHeaders());
      fetchFlows();
    } catch (e) {
      console.error("Local sync error:", e);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/api/automatizaciones/${confirmDelete}`, getAuthHeaders());
      await removeDirHandle(confirmDelete).catch(() => {});
      toast.success("Flujo eliminado");
      setConfirmDelete(null);
      fetchFlows();
      if (selectedFlow?.id === confirmDelete) { setView("list"); setSelectedFlow(null); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando flujo");
    }
  };

  const openDetail = async (flow) => {
    try {
      const res = await axios.get(`${API}/api/automatizaciones/${flow.id}`, getAuthHeaders());
      setSelectedFlow(res.data);
      setView("detail");
    } catch {
      toast.error("Error cargando detalle");
    }
  };

  const toggleFlowState = async (e, flow) => {
    e.stopPropagation();
    const newState = flow.estado === "activo" ? "pausado" : "activo";
    try {
      await axios.put(`${API}/api/automatizaciones/${flow.id}`, { estado: newState }, getAuthHeaders());
      toast.success(`Flujo ${newState === "activo" ? "activado" : "pausado"}`);
      fetchFlows();
    } catch {
      toast.error("Error cambiando estado");
    }
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getFrecuenciaLabel = (f) => FRECUENCIAS.find(x => x.value === f)?.label || f;
  const getOrigenLabel = (t) => ORIGENES.find(x => x.value === t)?.label || t;
  const getOrigenIcon = (t) => {
    const o = ORIGENES.find(x => x.value === t);
    return o ? o.icon : FolderOpen;
  };

  const getSchedulerBadge = (flow) => {
    const status = flow.scheduler_status;
    if (!status || status === "idle") return null;
    if (status === "awaiting_local_sync") return <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">Esperando sync local</Badge>;
    if (status === "awaiting_upload") return <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">Esperando carga</Badge>;
    if (status === "triggered") return <Badge variant="outline" className="text-xs text-green-600 border-green-300">Ejecutado</Badge>;
    return null;
  };

  if (view === "detail" && selectedFlow) {
    return (
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <main className="flex-1 overflow-auto p-6 ml-0 md:ml-64">
          <FlowDetail
            flow={selectedFlow}
            onBack={() => { setView("list"); setSelectedFlow(null); fetchFlows(); }}
            getAuthHeaders={getAuthHeaders}
            formatDate={formatDate}
            getFrecuenciaLabel={getFrecuenciaLabel}
            getOrigenLabel={getOrigenLabel}
            getOrigenIcon={getOrigenIcon}
            onEdit={(f) => { setEditingFlow(f); setShowCreateDialog(true); }}
            onRefresh={() => openDetail(selectedFlow)}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6 ml-0 md:ml-64">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="automatizaciones-title">
                <Zap className="w-6 h-6 text-amber-500" />
                Automatizaciones
              </h1>
              <p className="text-sm text-slate-500 mt-1">Configura flujos automáticos de sincronización de datos</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Microsoft connection status badge */}
              {msConnection?.connected ? (
                <Button variant="outline" size="sm" onClick={() => setShowMsConfig(true)} className="text-green-700 border-green-300 bg-green-50" data-testid="ms-connected-btn">
                  <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                  Microsoft: {msConnection.ms_user_email}
                </Button>
              ) : msConfig?.configured ? (
                <Button variant="outline" size="sm" onClick={() => setShowMsConfig(true)} className="text-amber-700 border-amber-300 bg-amber-50" data-testid="ms-config-btn">
                  <Cloud className="w-3.5 h-3.5 mr-1.5" />
                  Conectar Microsoft
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setShowMsConfig(true)} data-testid="ms-setup-btn">
                  <Settings className="w-3.5 h-3.5 mr-1.5" />
                  Configurar Microsoft
                </Button>
              )}
              <Button onClick={() => { setEditingFlow(null); setShowCreateDialog(true); }} data-testid="create-flow-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Flujo
              </Button>
            </div>
          </div>

          {/* Flow List */}
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : flows.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Zap className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">No hay flujos configurados</p>
                <Button onClick={() => { setEditingFlow(null); setShowCreateDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Crear primer flujo
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {flows.map((f) => {
                const OrigenIcon = getOrigenIcon(f.origen?.tipo);
                return (
                  <Card key={f.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openDetail(f)} data-testid={`flow-card-${f.id}`}>
                    <CardContent className="py-4">
                      <div className="flex flex-col gap-3">
                        {/* Row 1: Icon + Name + Badges */}
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg shrink-0 ${f.estado === "activo" ? "bg-green-100" : f.estado === "pausado" ? "bg-yellow-100" : "bg-red-100"}`}>
                            <Zap className={`w-5 h-5 ${f.estado === "activo" ? "text-green-600" : f.estado === "pausado" ? "text-yellow-600" : "text-red-600"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-semibold text-slate-900 truncate max-w-[200px] md:max-w-none">{f.nombre}</h3>
                              <Badge variant={f.estado === "activo" ? "default" : "secondary"} className="text-xs shrink-0">
                                {f.estado}
                              </Badge>
                              {getSchedulerBadge(f)}
                            </div>
                            <p className="text-sm text-slate-500 truncate">{f.descripcion || "Sin descripción"}</p>
                          </div>
                        </div>
                        {/* Row 2: Meta info + Actions */}
                        <div className="flex flex-wrap items-center gap-3 md:gap-6 text-sm text-slate-500 pl-0 md:pl-12">
                          <div className="flex items-center gap-1" title="Origen">
                            <OrigenIcon className="w-4 h-4" />
                            <span className="text-xs md:text-sm">{getOrigenLabel(f.origen?.tipo)}</span>
                          </div>
                          <div className="flex items-center gap-1" title="Frecuencia">
                            <Clock className="w-4 h-4" />
                            <span className="text-xs md:text-sm">{getFrecuenciaLabel(f.programacion?.frecuencia)}</span>
                          </div>
                          <div className="flex items-center gap-1" title="Registros">
                            <Database className="w-4 h-4" />
                            <span>{f.total_registros || 0}</span>
                          </div>
                          {f.next_execution && f.programacion?.activo && (
                            <div className="flex items-center gap-1 text-xs" title="Próxima ejecución">
                              <Timer className="w-3.5 h-3.5 text-indigo-500" />
                              <span className="text-indigo-600">{formatDate(f.next_execution)}</span>
                            </div>
                          )}
                          {f.ultima_ejecucion && (
                            <div className="flex items-center gap-1" title="Última ejecución">
                              {f.ultima_ejecucion.estado === "exitoso" ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                              <span className="text-xs">{formatDate(f.ultima_ejecucion.finalizado_en)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 ml-auto">
                            <Button size="icon" variant="ghost" onClick={(e) => toggleFlowState(e, f)} title={f.estado === "activo" ? "Pausar" : "Activar"} data-testid={`toggle-flow-${f.id}`}>
                              {f.estado === "activo" ? <Pause className="w-4 h-4 text-yellow-600" /> : <Play className="w-4 h-4 text-green-600" />}
                            </Button>
                            <Button size="icon" variant="ghost" className="text-red-500" onClick={(e) => { e.stopPropagation(); setConfirmDelete(f.id); }}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <FlowFormDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          flow={editingFlow}
          getAuthHeaders={getAuthHeaders}
          msConnection={msConnection}
          onSaved={() => { fetchFlows(); setShowCreateDialog(false); if (selectedFlow) openDetail(selectedFlow); }}
        />

        {/* Delete Confirm */}
        <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminar Flujo</AlertDialogTitle>
              <AlertDialogDescription>Se eliminará el flujo y todos sus datos almacenados. Esta acción es irreversible.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-flow">Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Microsoft Config Dialog */}
        <MicrosoftConfigDialog
          open={showMsConfig}
          onOpenChange={setShowMsConfig}
          getAuthHeaders={getAuthHeaders}
          msConfig={msConfig}
          msConnection={msConnection}
          onUpdated={fetchMsStatus}
        />
      </main>
    </div>
  );
}

// ============== FLOW FORM DIALOG ==============
function FlowFormDialog({ open, onOpenChange, flow, getAuthHeaders, msConnection, onSaved }) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [origenTipo, setOrigenTipo] = useState("manual");
  const [origenRuta, setOrigenRuta] = useState("");
  const [origenArchivo, setOrigenArchivo] = useState("");
  const [frecuencia, setFrecuencia] = useState("manual");
  const [moduloDestino, setModuloDestino] = useState("");
  const [coleccionDestino, setColeccionDestino] = useState("");
  const [colecciones, setColecciones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedDirName, setSelectedDirName] = useState("");
  const [cloudFileId, setCloudFileId] = useState("");
  const [cloudDriveId, setCloudDriveId] = useState("");
  const [cloudFileName, setCloudFileName] = useState("");
  const [showFileBrowser, setShowFileBrowser] = useState(false);

  useEffect(() => {
    if (open) {
      if (flow) {
        setNombre(flow.nombre || "");
        setDescripcion(flow.descripcion || "");
        setOrigenTipo(flow.origen?.tipo || "manual");
        setOrigenRuta(flow.origen?.ruta || "");
        setOrigenArchivo(flow.origen?.nombre_archivo || "*.xlsx");
        setFrecuencia(flow.programacion?.frecuencia || "manual");
        setModuloDestino(flow.modulo_destino || "");
        setColeccionDestino(flow.coleccion_destino || "");
        setSelectedDirName(flow.origen?.ruta || "");
        setCloudFileId(flow.origen?.cloud_file_id || "");
        setCloudDriveId(flow.origen?.cloud_drive_id || "");
        setCloudFileName(flow.origen?.cloud_file_name || "");
      } else {
        setNombre(""); setDescripcion(""); setOrigenTipo("manual"); setOrigenRuta("");
        setOrigenArchivo("*.xlsx"); setFrecuencia("manual"); setModuloDestino("");
        setColeccionDestino(""); setSelectedDirName(""); setCloudFileId(""); setCloudDriveId(""); setCloudFileName("");
      }
      setShowFileBrowser(false);
      // Fetch collections
      axios.get(`${API}/api/base-datos-flujos/colecciones`, getAuthHeaders())
        .then(res => setColecciones(res.data || []))
        .catch(() => {});
    }
  }, [open, flow]);

  const handleSelectFolder = async () => {
    if (!supportsFileSystemAccess) {
      toast.error("Su navegador no soporta acceso a carpetas locales. Use Chrome o Edge.");
      return;
    }
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: "read" });
      setSelectedDirName(dirHandle.name);
      setOrigenRuta(dirHandle.name);
      // Store temporarily - will be saved with flow ID after creation
      window._tempDirHandle = dirHandle;
      toast.success(`Carpeta seleccionada: ${dirHandle.name}`);
    } catch (e) {
      if (e.name !== "AbortError") {
        toast.error("Error seleccionando carpeta");
      }
    }
  };

  const handleSave = async () => {
    if (!nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const origenData = {
        tipo: origenTipo,
        ruta: origenRuta || selectedDirName,
        nombre_archivo: origenArchivo || "*.xlsx",
      };
      // Add cloud file info if applicable
      if (origenTipo === "onedrive" && cloudFileId) {
        origenData.cloud_file_id = cloudFileId;
        origenData.cloud_drive_id = cloudDriveId;
        origenData.cloud_file_name = cloudFileName;
      }
      const payload = {
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
        origen: origenData,
        programacion: { activo: frecuencia !== "manual", frecuencia },
        modulo_destino: moduloDestino,
        coleccion_destino: coleccionDestino,
      };
      let flowId;
      if (flow) {
        await axios.put(`${API}/api/automatizaciones/${flow.id}`, payload, getAuthHeaders());
        flowId = flow.id;
        toast.success("Flujo actualizado");
      } else {
        const res = await axios.post(`${API}/api/automatizaciones/`, payload, getAuthHeaders());
        flowId = res.data.id;
        toast.success("Flujo creado");
      }

      // Save directory handle to IndexedDB for local origin
      if (origenTipo === "local" && window._tempDirHandle && flowId) {
        await saveDirHandle(flowId, window._tempDirHandle);
        window._tempDirHandle = null;
      }

      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando flujo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="flow-form-dialog">
        <DialogHeader>
          <DialogTitle>{flow ? "Editar Flujo" : "Nuevo Flujo de Automatización"}</DialogTitle>
          <DialogDescription>Configure los parámetros del flujo de sincronización de datos</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label>Nombre del flujo *</Label>
            <Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Carga Facturación" data-testid="flow-name-input" />
          </div>
          <div>
            <Label>Descripción</Label>
            <Textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Descripción del flujo..." rows={2} />
          </div>

          {/* Origen de datos */}
          <div>
            <Label>Origen de datos</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {ORIGENES.map(o => {
                const Icon = o.icon;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => o.available && setOrigenTipo(o.value)}
                    className={`flex items-center gap-2 p-3 rounded-lg border text-left text-xs transition-colors ${
                      origenTipo === o.value
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                        : o.available
                        ? "border-slate-200 hover:border-slate-400 text-slate-700"
                        : "border-slate-100 bg-slate-50 text-slate-400 cursor-not-allowed"
                    }`}
                    data-testid={`origen-${o.value}`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <div>
                      <span className="font-medium">{o.label}</span>
                      {!o.available && <span className="block text-[10px] text-slate-400 mt-0.5">Próximamente</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Local folder config */}
          {origenTipo === "local" && (
            <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                <FolderSync className="w-4 h-4" />
                Carpeta Local
              </div>
              {supportsFileSystemAccess ? (
                <>
                  <Button type="button" variant="outline" size="sm" onClick={handleSelectFolder} className="w-full" data-testid="select-folder-btn">
                    <FolderOpen className="w-4 h-4 mr-2" />
                    {selectedDirName ? `Carpeta: ${selectedDirName}` : "Seleccionar Carpeta"}
                  </Button>
                  <p className="text-[10px] text-amber-600">
                    Al seleccionar, el navegador le pedirá permiso de lectura. Mientras la plataforma esté abierta, sincronizará automáticamente los archivos nuevos.
                  </p>
                </>
              ) : (
                <div className="p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700">
                  Su navegador no soporta el acceso directo a carpetas. Use Google Chrome o Microsoft Edge para esta funcionalidad. Como alternativa, puede subir archivos manualmente.
                </div>
              )}
              <div>
                <Label className="text-xs">Patrón de archivos a buscar</Label>
                <Input value={origenArchivo} onChange={e => setOrigenArchivo(e.target.value)} placeholder="*.xlsx" className="text-sm" />
                <p className="text-[10px] text-slate-500 mt-0.5">Extensiones soportadas: .xlsx, .xls, .csv</p>
              </div>
            </div>
          )}

          {/* Cloud origin config */}
          {origenTipo === "onedrive" && (
            <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
                <Cloud className="w-4 h-4" />
                OneDrive / SharePoint
              </div>
              {msConnection?.connected ? (
                <>
                  <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded border border-green-200">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Conectado como: {msConnection.ms_user_email}
                  </div>
                  {cloudFileName ? (
                    <div className="flex items-center justify-between bg-white p-2 rounded border text-xs">
                      <div className="flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-green-600" />
                        <span className="font-medium">{cloudFileName}</span>
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="text-xs h-7" onClick={() => setShowFileBrowser(true)} data-testid="change-cloud-file-btn">
                        Cambiar
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowFileBrowser(true)} className="w-full" data-testid="browse-cloud-files-btn">
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Seleccionar Archivo de OneDrive/SharePoint
                    </Button>
                  )}
                  <p className="text-[10px] text-blue-600">
                    El scheduler descargará automáticamente este archivo según la frecuencia configurada.
                  </p>
                </>
              ) : (
                <div className="p-3 bg-white rounded-lg border border-blue-200 text-center">
                  <Cloud className="w-8 h-8 mx-auto text-blue-300 mb-2" />
                  <p className="text-sm font-medium text-blue-700">Conecte su cuenta de Microsoft</p>
                  <p className="text-xs text-blue-500 mt-1">
                    Configure su cuenta de Microsoft desde el botón "Configurar Microsoft" en el encabezado para habilitar el acceso a OneDrive/SharePoint.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Google Drive - coming soon */}
          {origenTipo === "google_drive" && (
            <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-800">
                <Cloud className="w-4 h-4" />
                Google Drive
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200 text-center">
                <Cloud className="w-8 h-8 mx-auto text-blue-300 mb-2" />
                <p className="text-sm font-medium text-blue-700">Próximamente</p>
                <p className="text-xs text-blue-500 mt-1">La integración con Google Drive estará disponible próximamente.</p>
              </div>
            </div>
          )}

          <div>
            <Label>Frecuencia de ejecución</Label>
            <Select value={frecuencia} onValueChange={setFrecuencia}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {FRECUENCIAS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {frecuencia !== "manual" && (
              <p className="text-[10px] text-slate-500 mt-1">
                {origenTipo === "local"
                  ? "El sistema sincronizará automáticamente desde la carpeta seleccionada mientras la plataforma esté abierta."
                  : origenTipo === "manual"
                  ? "Recibirá un recordatorio cuando sea hora de cargar el archivo."
                  : "La integración con la nube ejecutará la sincronización automáticamente."}
              </p>
            )}
          </div>
          <div>
            <Label>Almacenar datos en (Base de Datos Flujos)</Label>
            <Select value={coleccionDestino} onValueChange={setColeccionDestino}>
              <SelectTrigger data-testid="coleccion-destino-select">
                <SelectValue placeholder="Seleccione una pestaña destino..." />
              </SelectTrigger>
              <SelectContent>
                {colecciones.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">Los datos cargados se almacenarán en esta pestaña de Base de Datos Flujos</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-flow-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {flow ? "Guardar Cambios" : "Crear Flujo"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Cloud File Browser */}
      <CloudFileBrowser
        open={showFileBrowser}
        onOpenChange={setShowFileBrowser}
        getAuthHeaders={getAuthHeaders}
        onFileSelected={(fileInfo) => {
          setCloudFileId(fileInfo.id);
          setCloudDriveId(fileInfo.driveId || "");
          setCloudFileName(fileInfo.name);
          setOrigenRuta(fileInfo.path || fileInfo.name);
          setShowFileBrowser(false);
          toast.success(`Archivo seleccionado: ${fileInfo.name}`);
        }}
      />
    </Dialog>
  );
}

// ============== FLOW DETAIL VIEW ==============
function FlowDetail({ flow, onBack, getAuthHeaders, formatDate, getFrecuenciaLabel, getOrigenLabel, getOrigenIcon, onEdit, onRefresh }) {
  const [tab, setTab] = useState("config");
  const [executions, setExecutions] = useState([]);
  const [dataset, setDataset] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingTab, setLoadingTab] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [hasDirHandle, setHasDirHandle] = useState(false);

  useEffect(() => {
    if (flow.origen?.tipo === "local") {
      getDirHandle(flow.id).then(h => setHasDirHandle(!!h)).catch(() => {});
    }
  }, [flow.id, flow.origen?.tipo]);

  useEffect(() => { if (tab === "history") fetchExecs(); else if (tab === "data") fetchData(); }, [tab]);

  const fetchExecs = async () => {
    setLoadingTab(true);
    try {
      const res = await axios.get(`${API}/api/automatizaciones/${flow.id}/ejecuciones`, getAuthHeaders());
      setExecutions(res.data);
    } catch { toast.error("Error cargando historial"); }
    finally { setLoadingTab(false); }
  };

  const fetchData = async () => {
    setLoadingTab(true);
    try {
      const res = await axios.get(`${API}/api/automatizaciones/${flow.id}/datos?limit=100`, getAuthHeaders());
      setDataset(res.data);
    } catch { toast.error("Error cargando datos"); }
    finally { setLoadingTab(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/api/automatizaciones/${flow.id}/upload`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`${res.data.registros_nuevos} registros importados`);
      onRefresh();
      if (tab === "history") fetchExecs();
      else if (tab === "data") fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error procesando archivo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSyncNow = async () => {
    if (!supportsFileSystemAccess) {
      toast.error("File System Access API no disponible en este navegador");
      return;
    }
    setSyncing(true);
    try {
      let handle = await getDirHandle(flow.id);
      if (!handle) {
        handle = await window.showDirectoryPicker({ mode: "read" });
        await saveDirHandle(flow.id, handle);
        setHasDirHandle(true);
      }

      const permission = await handle.queryPermission({ mode: "read" });
      if (permission !== "granted") {
        const req = await handle.requestPermission({ mode: "read" });
        if (req !== "granted") { toast.error("Permiso de lectura denegado"); setSyncing(false); return; }
      }

      const pattern = flow.origen?.nombre_archivo || "*.xlsx";
      const extensions = pattern.replace("*", "").split(",").map(e => e.trim().toLowerCase());
      let synced = 0;

      for await (const entry of handle.values()) {
        if (entry.kind !== "file") continue;
        const name = entry.name.toLowerCase();
        if (!extensions.some(ext => name.endsWith(ext))) continue;

        const file = await entry.getFile();
        const formData = new FormData();
        formData.append("file", file);
        await axios.post(`${API}/api/automatizaciones/${flow.id}/upload`, formData, {
          ...getAuthHeaders(),
          headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" },
        });
        synced++;
      }

      if (synced > 0) {
        toast.success(`${synced} archivo(s) sincronizado(s)`);
        await axios.post(`${API}/api/automatizaciones/${flow.id}/complete-execution`, {}, getAuthHeaders());
      } else {
        toast.info("No se encontraron archivos nuevos en la carpeta");
      }
      onRefresh();
      if (tab === "history") fetchExecs();
      else if (tab === "data") fetchData();
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Error durante la sincronización");
    } finally {
      setSyncing(false);
    }
  };

  const handleExecuteNow = async () => {
    try {
      await axios.post(`${API}/api/automatizaciones/${flow.id}/execute`, {}, getAuthHeaders());
      toast.success("Ejecución disparada");
      if (flow.origen?.tipo === "local") {
        handleSyncNow();
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error ejecutando flujo");
    }
  };

  const handleSelectFolder = async () => {
    try {
      const dirHandle = await window.showDirectoryPicker({ mode: "read" });
      await saveDirHandle(flow.id, dirHandle);
      setHasDirHandle(true);
      toast.success(`Carpeta vinculada: ${dirHandle.name}`);
      // Update flow with the folder name
      await axios.put(`${API}/api/automatizaciones/${flow.id}`, {
        origen: { tipo: "local", ruta: dirHandle.name, nombre_archivo: flow.origen?.nombre_archivo || "*.xlsx" }
      }, getAuthHeaders());
      onRefresh();
    } catch (e) {
      if (e.name !== "AbortError") toast.error("Error seleccionando carpeta");
    }
  };

  const OrigenIcon = getOrigenIcon(flow.origen?.tipo);

  const tabs = [
    { id: "config", label: "Configuración", icon: Settings },
    { id: "data", label: `Datos (${flow.total_registros || 0})`, icon: Database },
    { id: "history", label: "Historial", icon: History },
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="flow-detail-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> Volver
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            {flow.nombre}
            <Badge variant={flow.estado === "activo" ? "default" : "secondary"}>{flow.estado}</Badge>
          </h1>
          <p className="text-sm text-slate-500">{flow.descripcion}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Sync/Execute button */}
          {flow.origen?.tipo === "local" && supportsFileSystemAccess && (
            <Button variant="outline" onClick={handleSyncNow} disabled={syncing} data-testid="sync-now-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderSync className="w-4 h-4 mr-2" />}
              Sincronizar Ahora
            </Button>
          )}
          {flow.origen?.tipo !== "local" && (
            <Button variant="outline" onClick={handleExecuteNow} data-testid="execute-now-btn">
              <Play className="w-4 h-4 mr-2" /> Ejecutar Ahora
            </Button>
          )}
          <label>
            <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={handleUpload} disabled={uploading} />
            <Button variant="outline" asChild disabled={uploading}>
              <span className="cursor-pointer">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Subir Archivo
              </span>
            </Button>
          </label>
          <Button variant="outline" onClick={() => onEdit(flow)} data-testid="edit-flow-btn">
            <Edit className="w-4 h-4 mr-2" /> Editar
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${tab === t.id ? "border-blue-500 text-blue-600 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {loadingTab && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>}

      {!loadingTab && tab === "config" && (
        <div className="grid grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><OrigenIcon className="w-4 h-4" /> Origen</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm font-medium">{getOrigenLabel(flow.origen?.tipo)}</p>
              {flow.origen?.ruta && <p className="text-xs text-slate-500 mt-1">Ruta: {flow.origen.ruta}</p>}
              {flow.origen?.nombre_archivo && <p className="text-xs text-slate-500">Patrón: {flow.origen.nombre_archivo}</p>}
              {flow.origen?.tipo === "local" && (
                <div className="mt-3 space-y-2">
                  {hasDirHandle ? (
                    <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Carpeta vinculada en este navegador
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      No hay carpeta vinculada en este navegador
                    </div>
                  )}
                  {supportsFileSystemAccess && (
                    <Button variant="outline" size="sm" className="w-full text-xs" onClick={handleSelectFolder} data-testid="link-folder-btn">
                      <FolderOpen className="w-3.5 h-3.5 mr-1.5" />
                      {hasDirHandle ? "Cambiar Carpeta" : "Vincular Carpeta"}
                    </Button>
                  )}
                </div>
              )}
              {(flow.origen?.tipo === "google_drive" || flow.origen?.tipo === "onedrive") && (
                <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700 flex items-center gap-2">
                  <Cloud className="w-3.5 h-3.5" />
                  Integración en desarrollo. Use carga manual mientras tanto.
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Programación</CardTitle></CardHeader>
            <CardContent>
              <p className="text-sm font-medium">{getFrecuenciaLabel(flow.programacion?.frecuencia)}</p>
              <p className="text-xs text-slate-500 mt-1">
                Estado: {flow.programacion?.activo ? "Activa" : "Inactiva (manual)"}
              </p>
              {flow.next_execution && flow.programacion?.activo && (
                <div className="mt-2 p-2 bg-indigo-50 rounded text-xs text-indigo-700 flex items-center gap-2">
                  <Timer className="w-3.5 h-3.5" />
                  Próxima: {formatDate(flow.next_execution)}
                </div>
              )}
              {flow.scheduler_status && flow.scheduler_status !== "idle" && (
                <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700 flex items-center gap-2">
                  <RefreshCw className="w-3.5 h-3.5" />
                  {flow.scheduler_status === "awaiting_local_sync" && "Esperando sincronización local"}
                  {flow.scheduler_status === "awaiting_upload" && "Esperando carga de archivo"}
                  {flow.scheduler_status === "cloud_not_configured" && "Nube no configurada"}
                  {flow.scheduler_status === "triggered" && "Último disparo procesado"}
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Database className="w-4 h-4" /> Datos Almacenados</CardTitle></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-slate-900">{flow.total_registros || 0}</p>
              <p className="text-xs text-slate-500">registros en la base de datos</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> Ejecuciones</CardTitle></CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-slate-900">{flow.total_ejecuciones || 0}</p>
              <p className="text-xs text-slate-500">ejecuciones totales</p>
            </CardContent>
          </Card>
        </div>
      )}

      {!loadingTab && tab === "data" && (
        <Card>
          <CardContent className="p-0">
            {!dataset || dataset.total === 0 ? (
              <div className="py-8 text-center text-slate-500">
                <Database className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No hay datos almacenados. Sube un archivo para comenzar.</p>
              </div>
            ) : (
              <div className="overflow-auto max-h-[60vh]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs w-10">#</TableHead>
                      {dataset.columns.map(c => <TableHead key={c} className="text-xs">{c}</TableHead>)}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dataset.records.map((r, i) => (
                      <TableRow key={i} className="text-xs">
                        <TableCell className="text-slate-400">{i + 1}</TableCell>
                        {dataset.columns.map(c => <TableCell key={c} className="max-w-[200px] truncate">{r[c] ?? "-"}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="p-3 text-xs text-slate-500 border-t">
                  Mostrando {dataset.records.length} de {dataset.total} registros
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!loadingTab && tab === "history" && (
        <Card>
          <CardContent className="p-0">
            {executions.length === 0 ? (
              <div className="py-8 text-center text-slate-500">
                <History className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No hay ejecuciones registradas</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs">Estado</TableHead>
                    <TableHead className="text-xs">Archivo</TableHead>
                    <TableHead className="text-xs">Registros</TableHead>
                    <TableHead className="text-xs">Ejecutado por</TableHead>
                    <TableHead className="text-xs">Inicio</TableHead>
                    <TableHead className="text-xs">Fin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {executions.map(ex => (
                    <TableRow key={ex.id} className="text-xs">
                      <TableCell>
                        <Badge variant={ex.estado === "exitoso" ? "default" : ex.estado === "en_proceso" ? "secondary" : "destructive"} className="text-xs">
                          {ex.estado === "exitoso" && <CheckCircle className="w-3 h-3 mr-1" />}
                          {ex.estado === "con_errores" && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {ex.estado === "error" && <XCircle className="w-3 h-3 mr-1" />}
                          {ex.estado}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{ex.archivo}</TableCell>
                      <TableCell>{ex.registros_nuevos}/{ex.registros_procesados}</TableCell>
                      <TableCell>{ex.ejecutado_por}</TableCell>
                      <TableCell>{formatDate(ex.iniciado_en)}</TableCell>
                      <TableCell>{formatDate(ex.finalizado_en)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ============== MICROSOFT CONFIG DIALOG ==============
function MicrosoftConfigDialog({ open, onOpenChange, getAuthHeaders, msConfig, msConnection, onUpdated }) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (open && msConfig?.configured) {
      setClientId(msConfig.client_id || "");
      setTenantId(msConfig.tenant_id || "");
      setClientSecret("");
    } else if (open) {
      setClientId(""); setClientSecret(""); setTenantId("");
    }
  }, [open, msConfig]);

  const handleSaveConfig = async () => {
    if (!clientId.trim() || !tenantId.trim()) {
      toast.error("Client ID y Tenant ID son obligatorios");
      return;
    }
    if (!msConfig?.configured && !clientSecret.trim()) {
      toast.error("Client Secret es obligatorio para la primera configuración");
      return;
    }
    setSaving(true);
    try {
      const payload = { client_id: clientId.trim(), tenant_id: tenantId.trim(), client_secret: clientSecret.trim() || "unchanged" };
      await axios.post(`${API}/api/microsoft/config`, payload, getAuthHeaders());
      toast.success("Configuración de Azure AD guardada");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error guardando configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${API}/api/microsoft/auth/url`, getAuthHeaders());
      window.location.href = res.data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error iniciando conexión");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await axios.delete(`${API}/api/microsoft/connection`, getAuthHeaders());
      toast.success("Cuenta de Microsoft desconectada");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error desconectando");
    }
  };

  const handleDeleteConfig = async () => {
    try {
      await axios.delete(`${API}/api/microsoft/config`, getAuthHeaders());
      toast.success("Configuración eliminada");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error eliminando configuración");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ms-config-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-600" />
            Configuración de Microsoft
          </DialogTitle>
          <DialogDescription>
            Configure las credenciales de Azure AD para conectar OneDrive y SharePoint.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Connection Status */}
          {msConnection?.connected && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Cuenta conectada</p>
                    <p className="text-xs text-green-600">{msConnection.ms_user_name} ({msConnection.ms_user_email})</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50" onClick={handleDisconnect} data-testid="ms-disconnect-btn">
                  <Unlink className="w-3.5 h-3.5 mr-1" />
                  Desconectar
                </Button>
              </div>
            </div>
          )}

          {/* Azure AD Configuration */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">Credenciales de Azure AD</h3>
            <p className="text-xs text-slate-500">
              Registre una aplicación en{" "}
              <a href="https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                Microsoft Entra (Azure AD)
              </a>{" "}
              con los permisos: <strong>Files.Read.All</strong>, <strong>Sites.Read.All</strong>, <strong>User.Read</strong> y <strong>offline_access</strong>.
              Agregue como URI de redirección: <code className="text-xs bg-slate-100 px-1 rounded">{API}/api/microsoft/auth/callback</code>
            </p>

            <div>
              <Label className="text-xs">Application (Client) ID *</Label>
              <Input value={clientId} onChange={e => setClientId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="text-sm font-mono" data-testid="ms-client-id-input" />
            </div>
            <div>
              <Label className="text-xs">Directory (Tenant) ID *</Label>
              <Input value={tenantId} onChange={e => setTenantId(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="text-sm font-mono" data-testid="ms-tenant-id-input" />
            </div>
            <div>
              <Label className="text-xs">Client Secret {msConfig?.has_secret ? "(dejar vacío para mantener actual)" : "*"}</Label>
              <Input type="password" value={clientSecret} onChange={e => setClientSecret(e.target.value)} placeholder={msConfig?.has_secret ? "••••••••••" : "Ingrese el client secret"} className="text-sm font-mono" data-testid="ms-client-secret-input" />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveConfig} disabled={saving} className="flex-1" data-testid="ms-save-config-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Guardar Configuración
              </Button>
              {msConfig?.configured && !msConnection?.connected && (
                <Button variant="default" onClick={handleConnect} disabled={connecting} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="ms-connect-btn">
                  {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                  Conectar Cuenta Microsoft
                </Button>
              )}
            </div>

            {msConfig?.configured && (
              <Button variant="ghost" size="sm" className="w-full text-xs text-red-500 hover:text-red-700" onClick={handleDeleteConfig}>
                Eliminar toda la configuración de Azure AD
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============== CLOUD FILE BROWSER ==============
function CloudFileBrowser({ open, onOpenChange, getAuthHeaders, onFileSelected }) {
  const [mode, setMode] = useState("onedrive"); // "onedrive" | "sharepoint"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPath, setCurrentPath] = useState("");
  const [pathHistory, setPathHistory] = useState([]);
  // SharePoint state
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [drives, setDrives] = useState([]);
  const [selectedDrive, setSelectedDrive] = useState(null);

  useEffect(() => {
    if (open) {
      setMode("onedrive");
      setCurrentPath("");
      setPathHistory([]);
      setItems([]);
      setSites([]);
      setSelectedSite(null);
      setDrives([]);
      setSelectedDrive(null);
      loadOneDriveRoot();
    }
  }, [open]);

  const loadOneDriveRoot = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/microsoft/onedrive/files`, getAuthHeaders());
      setItems(res.data.items || []);
      setCurrentPath("");
      setPathHistory([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando archivos de OneDrive");
    } finally { setLoading(false); }
  };

  const navigateToFolder = async (folderName) => {
    setLoading(true);
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    try {
      const res = await axios.get(`${API}/api/microsoft/onedrive/files?path=${encodeURIComponent(newPath)}`, getAuthHeaders());
      setPathHistory(prev => [...prev, currentPath]);
      setCurrentPath(newPath);
      setItems(res.data.items || []);
    } catch (err) {
      toast.error("Error navegando carpeta");
    } finally { setLoading(false); }
  };

  const navigateBack = async () => {
    if (pathHistory.length === 0) return;
    const prevPath = pathHistory[pathHistory.length - 1];
    setLoading(true);
    try {
      const url = prevPath
        ? `${API}/api/microsoft/onedrive/files?path=${encodeURIComponent(prevPath)}`
        : `${API}/api/microsoft/onedrive/files`;
      const res = await axios.get(url, getAuthHeaders());
      setPathHistory(prev => prev.slice(0, -1));
      setCurrentPath(prevPath);
      setItems(res.data.items || []);
    } catch { toast.error("Error navegando"); }
    finally { setLoading(false); }
  };

  const loadSharePointSites = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/microsoft/sharepoint/sites`, getAuthHeaders());
      setSites(res.data.sites || []);
      setSelectedSite(null);
      setDrives([]);
      setSelectedDrive(null);
      setItems([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando sitios");
    } finally { setLoading(false); }
  };

  const selectSite = async (site) => {
    setLoading(true);
    setSelectedSite(site);
    try {
      const res = await axios.get(`${API}/api/microsoft/sharepoint/sites/${encodeURIComponent(site.id)}/drives`, getAuthHeaders());
      setDrives(res.data.drives || []);
      setSelectedDrive(null);
      setItems([]);
    } catch (err) {
      toast.error("Error cargando bibliotecas");
    } finally { setLoading(false); }
  };

  const selectDrive = async (drive) => {
    setLoading(true);
    setSelectedDrive(drive);
    try {
      const res = await axios.get(`${API}/api/microsoft/sharepoint/drives/${drive.id}/files`, getAuthHeaders());
      setItems(res.data.items || []);
      setCurrentPath("");
      setPathHistory([]);
    } catch { toast.error("Error cargando archivos"); }
    finally { setLoading(false); }
  };

  const navigateDriveFolder = async (folderName) => {
    if (!selectedDrive) return;
    setLoading(true);
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    try {
      const res = await axios.get(`${API}/api/microsoft/sharepoint/drives/${selectedDrive.id}/files?path=${encodeURIComponent(newPath)}`, getAuthHeaders());
      setPathHistory(prev => [...prev, currentPath]);
      setCurrentPath(newPath);
      setItems(res.data.items || []);
    } catch { toast.error("Error navegando"); }
    finally { setLoading(false); }
  };

  const navigateDriveBack = async () => {
    if (!selectedDrive || pathHistory.length === 0) return;
    const prevPath = pathHistory[pathHistory.length - 1];
    setLoading(true);
    try {
      const url = prevPath
        ? `${API}/api/microsoft/sharepoint/drives/${selectedDrive.id}/files?path=${encodeURIComponent(prevPath)}`
        : `${API}/api/microsoft/sharepoint/drives/${selectedDrive.id}/files`;
      const res = await axios.get(url, getAuthHeaders());
      setPathHistory(prev => prev.slice(0, -1));
      setCurrentPath(prevPath);
      setItems(res.data.items || []);
    } catch { toast.error("Error navegando"); }
    finally { setLoading(false); }
  };

  const handleSelectFile = (item) => {
    const fileInfo = {
      id: item.id,
      name: item.name,
      driveId: mode === "sharepoint" && selectedDrive ? selectedDrive.id : "",
      path: currentPath ? `${currentPath}/${item.name}` : item.name,
    };
    onFileSelected(fileInfo);
  };

  const isExcelOrCsv = (name) => {
    const lower = name.toLowerCase();
    return lower.endsWith(".xlsx") || lower.endsWith(".xls") || lower.endsWith(".csv");
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]" data-testid="cloud-file-browser">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-blue-600" />
            Seleccionar Archivo
          </DialogTitle>
          <DialogDescription>Navegue por sus archivos de OneDrive o SharePoint y seleccione el archivo a sincronizar.</DialogDescription>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="flex gap-1 border-b">
          <button
            onClick={() => { setMode("onedrive"); loadOneDriveRoot(); }}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${mode === "onedrive" ? "border-blue-500 text-blue-600 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-onedrive"
          >
            <HardDrive className="w-4 h-4" /> OneDrive
          </button>
          <button
            onClick={() => { setMode("sharepoint"); loadSharePointSites(); }}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${mode === "sharepoint" ? "border-blue-500 text-blue-600 font-medium" : "border-transparent text-slate-500 hover:text-slate-700"}`}
            data-testid="tab-sharepoint"
          >
            <Cloud className="w-4 h-4" /> SharePoint
          </button>
        </div>

        {/* Breadcrumbs */}
        {mode === "onedrive" && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <button onClick={loadOneDriveRoot} className="hover:text-blue-600">OneDrive</button>
            {currentPath && currentPath.split("/").map((part, i, arr) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                <span className={i === arr.length - 1 ? "font-medium text-slate-700" : ""}>{part}</span>
              </span>
            ))}
          </div>
        )}

        {mode === "sharepoint" && (
          <div className="flex items-center gap-1 text-xs text-slate-500">
            <button onClick={loadSharePointSites} className="hover:text-blue-600">SharePoint</button>
            {selectedSite && (
              <><ChevronRight className="w-3 h-3" /><button onClick={() => selectSite(selectedSite)} className="hover:text-blue-600">{selectedSite.name}</button></>
            )}
            {selectedDrive && (
              <><ChevronRight className="w-3 h-3" /><button onClick={() => selectDrive(selectedDrive)} className="hover:text-blue-600">{selectedDrive.name}</button></>
            )}
            {currentPath && currentPath.split("/").map((part, i, arr) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3" />
                <span className={i === arr.length - 1 ? "font-medium text-slate-700" : ""}>{part}</span>
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="min-h-[300px] max-h-[50vh] overflow-auto border rounded-lg">
          {loading ? (
            <div className="flex justify-center items-center h-48"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
          ) : mode === "sharepoint" && !selectedSite ? (
            /* SharePoint Sites */
            sites.length === 0 ? (
              <div className="p-8 text-center text-slate-500"><Cloud className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p>No se encontraron sitios de SharePoint</p></div>
            ) : (
              <div className="divide-y">
                {sites.map(site => (
                  <button key={site.id} onClick={() => selectSite(site)} className="w-full flex items-center gap-3 p-3 hover:bg-blue-50 text-left transition-colors" data-testid={`site-${site.id}`}>
                    <Cloud className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{site.name}</p>
                      <p className="text-xs text-slate-500 truncate">{site.webUrl}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                ))}
              </div>
            )
          ) : mode === "sharepoint" && selectedSite && !selectedDrive ? (
            /* SharePoint Drives */
            drives.length === 0 ? (
              <div className="p-8 text-center text-slate-500"><Database className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p>No se encontraron bibliotecas de documentos</p></div>
            ) : (
              <div className="divide-y">
                {drives.map(drive => (
                  <button key={drive.id} onClick={() => selectDrive(drive)} className="w-full flex items-center gap-3 p-3 hover:bg-blue-50 text-left transition-colors">
                    <Database className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{drive.name}</p>
                      <p className="text-xs text-slate-500">{drive.driveType}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </button>
                ))}
              </div>
            )
          ) : (
            /* File List (OneDrive or SharePoint drive) */
            <div className="divide-y">
              {(mode === "onedrive" ? pathHistory.length > 0 : pathHistory.length > 0) && (
                <button onClick={mode === "onedrive" ? navigateBack : navigateDriveBack} className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 text-left transition-colors text-slate-500">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="text-sm">Volver</span>
                </button>
              )}
              {items.length === 0 ? (
                <div className="p-8 text-center text-slate-500"><FolderOpen className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p>Carpeta vacía</p></div>
              ) : items.map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.type === "folder") {
                      mode === "onedrive" ? navigateToFolder(item.name) : navigateDriveFolder(item.name);
                    } else if (isExcelOrCsv(item.name)) {
                      handleSelectFile(item);
                    }
                  }}
                  className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                    item.type === "folder" ? "hover:bg-blue-50" : isExcelOrCsv(item.name) ? "hover:bg-green-50 cursor-pointer" : "opacity-50 cursor-not-allowed"
                  }`}
                  disabled={item.type === "file" && !isExcelOrCsv(item.name)}
                  data-testid={`file-item-${item.name}`}
                >
                  {item.type === "folder" ? (
                    <Folder className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  ) : isExcelOrCsv(item.name) ? (
                    <FileSpreadsheet className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <File className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 truncate">{item.name}</p>
                    <p className="text-xs text-slate-400">{formatSize(item.size)}</p>
                  </div>
                  {item.type === "folder" ? (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  ) : isExcelOrCsv(item.name) ? (
                    <Badge variant="outline" className="text-xs text-green-600 border-green-300">Seleccionar</Badge>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
